/**
 * @fileoverview LocalDateTime component that allows for users to set
 * a datetime (and a recurring day of the week) locally and have it display
 * with the adjusted date, time, and day of the week for all other users'
 * local timezones.
 */

import {NodeViewWrapper, NodeViewProps, Editor} from '@tiptap/react';
import {Node, mergeAttributes} from '@tiptap/core';
import {ReactNodeViewRenderer} from '@tiptap/react';
import {getLocalUTCTimezone} from 'components/timezones/timezoneHelper';
import Swal from 'sweetalert2';
import {useState} from 'react';

/**
 * I don't quite know what this does. My best guess is it creates a Node
 * view for the component, specifically geared towards servicing DateTimes.
 * If you do know what this does in more depth, please update this
 * documentation. - Cameron 2025-04-16.
 *
 */
export const LocalDateTime = Node.create({
  name: 'dateTime',
  group: 'inline',
  inline: true,
  atom: false,
  draggable: true,

  addAttributes() {
    return {
      dateTime: {default: null},
      showDate: {default: false},
      dayOfWeek: {default: null},
    };
  },

  parseHTML() {
    return [{tag: 'dateTime'}];
  },

  renderHTML({HTMLAttributes}) {
    return ['span', mergeAttributes(HTMLAttributes)];
  },

  addNodeView() {
    return ReactNodeViewRenderer(DateTimeView);
  },
});

/**
 * Simple record mapping days of the week to dropdown indexes.
 *
 * @note This is zero-indexed, starting with Sunday
 */
const WEEKDAY_INDEX: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

/**
 * Takes a given date and returns a simplified, ISO-friendly string
 *
 * @param date Date object in the format of Day-of-Week MMM DD YYYY HH:MM:SS GMT±TTTT (Timezone Name)
 * @returns the local date in the format of YYYY-MM-DD
 */
const getLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * String values we want to pass into the convertToDateObj to output as a Date object
 */
interface ConvertToDateObjProps {
  /** date in the format of YYYY-MM-DD */
  date?: string | null;

  /** time in the format of HH:MM */
  time: string;

  /** the day of the week (e.g. Wednesday) */
  dayOfWeek?: string | null;
}

/**
 * Returned values and their associated types from the convertToDateObj function
 */
interface convertedDateObj {
  /** Date object in the format of Day-of-Week MMM DD YYYY HH:MM:SS GMT±TTTT (Timezone Name) */
  selectedDate: Date;

  /** whether or not a LocalDateTime has an attached date we want to show */
  showDate: boolean;
}

/**
 * Converts string-typed dates, times, and days of the week to a Date-typed object. Also indicates
 * whether or not the date itself should be shown.
 *
 * @param date a specific date the user wants to set a LocalDateTime to
 * @param time the time a user wants to set a LocalDateTime to
 * @param dayOfWeek the day of week a user wants a LocalDateTime to recur on
 * @returns a Date object with the relevant information to display and whether or not the date itself should be shown
 */
const convertToDateObj = ({
  date,
  time,
  dayOfWeek,
}: ConvertToDateObjProps): convertedDateObj => {
  let selectedDate: Date;
  let showDate = false;

  // Get time components
  const [hour, minute] = time.split(':').map(Number);

  // Keeping this separate in case we want to separate recurring days of the week from their date
  // e.g. April 21st is a Monday, but maybe we want to recur on Wednesdays after
  if (date && dayOfWeek) {
    showDate = true;
    selectedDate = new Date(`${date}T${time}`);
  }

  // Specific date is supplied, but not a day of the week to recur on
  else if (date) {
    showDate = true;
    selectedDate = new Date(`${date}T${time}`);
  }

  // No specific date is supplied, just a day of the week to recur on
  else if (dayOfWeek) {
    const today = new Date();
    const targetIdx = WEEKDAY_INDEX[dayOfWeek];
    let diff = (targetIdx - today.getDay() + 7) % 7;
    if (
      diff === 0 &&
      (today.getHours() > hour ||
        (today.getHours() === hour && today.getMinutes() >= minute))
    ) {
      diff = 7;
    }

    selectedDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + diff,
      hour,
      minute,
    );
  }

  // Return the current date and time right now.
  else {
    const today = new Date();
    selectedDate = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hour,
      minute,
    );
  }

  return {selectedDate, showDate};
};

/**
 * Pieces of data we want to reassemble nicely into strings
 */
interface getUTCComponentsProps {
  /** Date object in the format of Day-of-Week MMM DD YYYY HH:MM:SS GMT±TTTT (Timezone Name) */
  dateObj?: Date | null;

  /** whether or not a LocalDateTime has an attached date we want to show */
  showDate: boolean;

  /** the day of the week for a time to recur on */
  recurringDay?: string | null;
}

/**
 * Returned values of getUTCComponents alongside their types
 */
interface UTCComponents {
  /** time in the format of HH:MM */
  time: string;

  /** date in the format of YYYY-MM-DD */
  date: string;

  /** the day of the week (e.g. Wednesday) */
  dayOfWeek: string;
}

/**
 * Responsible for taking: a Date object; whether or not the date is relevant;
 * and the day to repeat on (if desired), and breaking them into
 * core temporal components as strings.
 *
 * @note THIS FUNCTION IS RELATIVE TO THE USER'S LOCAL COMPUTER TIME!
 * @note IF OPTIONAL COMPONENTS AREN'T PROVIDED, THIS RETURNS THEIR VALUES AS EMPTY STRINGS!
 *
 * @param dateObj the actual Date object the user wants to split into pieces
 * @param showDate whether or not the user wants to display the date
 * @param recurringDay the day the user wants the LocalDateTime to repeat, if it exists
 * @returns time, date, and day of the week; all as strings
 */
const getUTCComponents = ({
  dateObj,
  showDate,
  recurringDay,
}: getUTCComponentsProps): UTCComponents => {
  if (!dateObj) {
    return {time: '', date: '', dayOfWeek: ''};
  }

  // Extract time components
  const adjustedHours = dateObj.getHours().toString().padStart(2, '0');
  const adjustedMinutes = dateObj.getMinutes().toString().padStart(2, '0');
  const time = `${adjustedHours}:${adjustedMinutes}`;

  // Only include if true
  const date = showDate ? getLocalDate(dateObj) : '';
  const dayOfWeek = recurringDay ?? '';

  return {time, date, dayOfWeek};
};

/**
 * Props for the DateTimeView's HTML view that the Swal brings up
 * when the user attempts to edit a LocalDateTime component.
 */
interface getDatePickerHTMLProps {
  /** the timezone of the user's device */
  timezone: string;

  /** the time currently set in the Swal */
  initialTime: string;

  /** the date currently set in the Swal */
  initialDate: string;

  /** the day of the week currently set in the Swal */
  initialDayOfWeek: string;
}

/**
 * Handles logic for displaying and editing LocalDateTime components.
 *
 * @param props NodeViewProps which stores LocalDateTime component attributes for editing and display.
 * @returns component which renders a datetime locally across all timezones, accounting for any and all differences.
 */
export const DateTimeView = (props: NodeViewProps): JSX.Element => {
  const {dateTime, showDate, dayOfWeek} = props.node.attrs;
  const [isEditable] = useState(props.editor.isEditable);
  const parsedDateTime = dateTime ? new Date(dateTime) : null;

  const {
    time: initialTime,
    date: initialDate,
    dayOfWeek: initialDayOfWeek,
  } = getUTCComponents({
    dateObj: parsedDateTime,
    showDate,
    recurringDay: dayOfWeek,
  });

  if (dateTime) {
    const dateObj = new Date(dateTime);

    const getDatePickerHTML = ({
      timezone,
      initialTime,
      initialDate,
      initialDayOfWeek,
    }: getDatePickerHTMLProps) => `
      <p>This is the date and time in your local timezone, which is ${timezone}.</p>

      <label for="time">Time:</label>
      <input type="time" id="time" name="time" value="${initialTime}" required><br><br>

      <label for="date">Date (Optional):</label>
      <input 
        type="date" 
        id="date" 
        name="date" 
        value="${initialDate}" 
        data-has-dow="${initialDayOfWeek || ''}"
        onchange="
          if (this.dataset.hasDow) {
            const t = (document.getElementById('time')?.value) || '00:00';
            const d = new Date(this.value + 'T' + t);
            document.getElementById('dayOfWeek').value =
              d.toLocaleDateString(navigator.language, { weekday: 'long' });
          }
        "
        /><br/><br/>

      <label>Repeats On (optional):</label>
        <select id="dayOfWeek">
          <option value="">— Doesn't Repeat —</option>
          ${Object.keys(WEEKDAY_INDEX)
            .map(
              day =>
                `<option value="${day}" ${day === initialDayOfWeek ? 'selected' : ''}>${day}</option>`,
            )
            .join('')}
        </select>
      `;
    /**
     * Responsible for logic which handles updating of existing LocalDateTime components.
     */
    const handleChangeDateTime = () => {
      if (!isEditable) return;

      // Preconfirmation for data when updating an existing LocalDateTime
      const datePickerSwal = Swal.mixin({
        title: 'Pick a time',
        html: getDatePickerHTML({
          timezone: getLocalUTCTimezone(),
          initialTime,
          initialDate,
          initialDayOfWeek,
        }),
        preConfirm: () => {
          // @ts-ignore
          const time = document.getElementById('time').value; // Time will be in 24-hour time
          // @ts-ignore
          const date = document.getElementById('date').value;
          // @ts-ignore
          const dayOfWeek = document.getElementById('dayOfWeek').value;

          console.log('CM/pre-confirm', date, time, dayOfWeek);

          convertToDateObj({date, time, dayOfWeek});
          return {date, time, dayOfWeek};
        },
      });

      // Fire update to the existing LocalDateTime
      datePickerSwal.fire().then(e => {
        if (e.isConfirmed) {
          const {date, time, dayOfWeek} = e.value;
          const {selectedDate, showDate} = convertToDateObj({
            date,
            time,
            dayOfWeek,
          });
          props.updateAttributes({
            dateTime: selectedDate.toString(),
            showDate,
            dayOfWeek,
          });
        }
      });
    };

    if (!parsedDateTime) {
      return (
        <NodeViewWrapper className="d-inline">
          <span className="badge text-bg-secondary">Invalid Date</span>
        </NodeViewWrapper>
      );
    }

    const timeStr = dateObj.toLocaleTimeString(navigator.language, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });

    let label: string;

    // When both a recurring day and a date are set
    if (dayOfWeek && showDate) {
      const localWeekday = dateObj.toLocaleDateString(navigator.language, {
        weekday: 'long',
      });

      const startDateStr = dateObj.toLocaleDateString(navigator.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });

      label = `${localWeekday}s at ${timeStr}, starting ${startDateStr}`;
    }

    // When only a recurring day is set
    else if (dayOfWeek) {
      const localWeekday = dateObj.toLocaleDateString(navigator.language, {
        weekday: 'long',
      });

      label = `${localWeekday}s at ${timeStr}`;
    }

    // Set the label to be the time and add the date if it was added
    else {
      const options: Intl.DateTimeFormatOptions = {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
        ...(showDate && {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
      };

      label = dateObj.toLocaleString(navigator.language, options);
    }

    return (
      <NodeViewWrapper className="d-inline">
        <span
          className="badge text-bg-secondary"
          onClick={handleChangeDateTime}
        >
          {label}
        </span>
      </NodeViewWrapper>
    );
  }
};

/**
 * Responsible for handling logic related to the very first creation of a
 * (singular) LocalDateTime component.
 *
 * @param editor NodeViewProps editor used to fire the Swal.
 */
export const onInsertDateTime = (editor: Editor): void => {
  const datePickerHTML = (timezone: string) => `
    <p>This is the date and time in your local timezone, which is ${timezone}</p>
    <label>Time:</label>
    <input type="time" id="time" required /><br/><br/>

    <label>Date (optional):</label>
    <input type="date" id="date" /><br/><br/>

    <label>Repeats On (optional):</label>
    <select id="dayOfWeek">
      <option value="">— Doesn't Repeat —</option>
      ${Object.keys(WEEKDAY_INDEX)
        .map(day => `<option value="${day}">${day}</option>`)
        .join('')}
    </select>
    `;
  // Preconfirmation for data of the LocalDateTime
  const datePickerSwal = Swal.mixin({
    title: 'Pick a time',
    html: datePickerHTML(getLocalUTCTimezone()),
    preConfirm: () => {
      const time = (document.getElementById('time') as HTMLInputElement).value; // time will be in 24-hour time
      const date = (document.getElementById('date') as HTMLInputElement).value;
      const dayOfWeek = (
        document.getElementById('dayOfWeek') as HTMLSelectElement
      ).value;

      if (!time) {
        Swal.showValidationMessage('You must provide at least a time.');
        return false;
      }

      return {date, time, dayOfWeek};
    },
  });

  // Fire creation of the LocalDateTime
  datePickerSwal.fire().then(e => {
    if (e.isConfirmed) {
      const {date, time, dayOfWeek} = e.value;
      const {selectedDate, showDate} = convertToDateObj({
        date,
        time,
        dayOfWeek,
      });

      editor
        .chain()
        .focus()
        .insertContent({
          type: 'dateTime',
          attrs: {
            dateTime: selectedDate.toISOString(),
            showDate,
            dayOfWeek,
          },
        })
        .run();
    }
  });
};
